import ProxyServerConnector from './ProxyServerConnector';
import { createSseFrameBuffer, parseSseEvent } from './sseEventStream';
import { MAX_CONSECUTIVE_FAILURES, backoffDelayMs, classifyFailure, type SseFailure } from './sseRetryPolicy';

// The server polls every 2s and sends no heartbeat comments, so three missed ticks means the
// connection is dead rather than the job being slow.
export const DEFAULT_IDLE_TIMEOUT_MS = 6_000;

// The server emitter is created with Long.MAX_VALUE and states exist that never turn terminal,
// so nothing but this stops a subscription from living forever.
export const DEFAULT_DEADLINE_MS = 15 * 60_000;

export type SseCloseReason = 'aborted' | 'completed' | 'failed' | 'timeout';

export type SseMessageHandler<TRes> = (data: TRes, unsubscribe: () => void) => void;

export type SseSubscribeConfig<TRes, TReq> = {
    eventName: string;
    body?: TReq;
    onMessage: SseMessageHandler<TRes>;
    onClose?: (reason: SseCloseReason) => void;
    idleTimeoutMs?: number;
    deadlineMs?: number;
};

class SseHttpError extends Error {
    constructor(readonly status: number) {
        super(`SSE_REQUEST_FAILED_${status}`);
    }
}

const toFailure = (error: unknown): SseFailure =>
    error instanceof SseHttpError ? { kind: 'http', status: error.status } : { kind: 'network' };

const wait = (ms: number, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
        const onAbort = () => {
            clearTimeout(timer);
            resolve();
        };

        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);

        signal.addEventListener('abort', onAbort, { once: true });
    });

// Read per call: setGatewayPrefix runs during login, after this module is imported.
function resolveStreamUrl(basePath: string, path: string): string {
    const prefix = (ProxyServerConnector.gatewayPrefix || '/gateway').replace(/\/$/, '');

    return `${prefix}${basePath}${path}`;
}

export function gatewayStream(basePath: string) {
    return {
        subscribe<TRes, TReq = undefined>(
            path: string,
            {
                eventName,
                body,
                onMessage,
                onClose,
                idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
                deadlineMs = DEFAULT_DEADLINE_MS,
            }: SseSubscribeConfig<TRes, TReq>,
        ): () => void {
            // The URL is resolved per attempt; this is only for log messages.
            const label = `${basePath}${path}`;
            // Outer signal ends the whole subscription. Each attempt gets its own inner one.
            const subscription = new AbortController();
            let isSettled = false;

            const finish = (reason: SseCloseReason) => {
                if (isSettled) {
                    return;
                }

                isSettled = true;
                clearTimeout(deadlineTimer);
                subscription.abort();
                onClose?.(reason);
            };

            const deadlineTimer = setTimeout(() => finish('timeout'), deadlineMs);
            const unsubscribe = () => finish('aborted');

            const dispatchFrame = (frame: string) => {
                // A terminal event may unsubscribe mid-chunk; stop dispatching the rest.
                if (isSettled) {
                    return;
                }

                const parsed = parseSseEvent(frame);

                if (!parsed || parsed.event !== eventName) {
                    return;
                }

                let data: TRes;

                try {
                    data = JSON.parse(parsed.data) as TRes;
                } catch {
                    // Ignore malformed payloads rather than tearing down the stream.
                    return;
                }

                try {
                    onMessage(data, unsubscribe);
                } catch (error) {
                    // Otherwise the throw escapes through pipeTo, is read as a network error, and
                    // burns the retry budget while hiding its own stack.
                    console.error(`SSE handler failed: ${label}`, error);
                }
            };

            const connectOnce = async (attempt: { openedAt: number }) => {
                const connection = new AbortController();
                const abortConnection = () => connection.abort();
                subscription.signal.addEventListener('abort', abortConnection, { once: true });

                let idleTimer: ReturnType<typeof setTimeout> | undefined;

                const armIdleTimer = () => {
                    clearTimeout(idleTimer);
                    idleTimer = setTimeout(abortConnection, idleTimeoutMs);
                };

                try {
                    const headers: Record<string, string> = { Accept: 'text/event-stream' };
                    // Re-read every attempt: the token rotates on a 20-minute expiry buffer.
                    const token = ProxyServerConnector.getToken();

                    if (token) {
                        headers.Authorization = `Bearer ${token}`;
                    }

                    if (body !== undefined) {
                        headers['Content-Type'] = 'application/json';
                    }

                    // Re-read every attempt: setGatewayPrefix can change mid-subscription.
                    const response = await fetch(resolveStreamUrl(basePath, path), {
                        method: body === undefined ? 'GET' : 'POST',
                        headers,
                        body: body === undefined ? undefined : JSON.stringify(body),
                        signal: connection.signal,
                    });

                    if (!response.ok || !response.body) {
                        try {
                            // Release the connection; an unread error body keeps it open.
                            await response.body?.cancel();
                        } catch {
                            // Never let cleanup mask the real failure.
                        }

                        throw new SseHttpError(response.status);
                    }

                    const frames = createSseFrameBuffer();
                    armIdleTimer();
                    attempt.openedAt = Date.now();

                    await response.body.pipeThrough(new TextDecoderStream()).pipeTo(
                        new WritableStream({
                            write(chunk: string) {
                                armIdleTimer();
                                frames.push(chunk).forEach(dispatchFrame);
                            },
                        }),
                        { signal: connection.signal },
                    );
                } finally {
                    clearTimeout(idleTimer);
                    subscription.signal.removeEventListener('abort', abortConnection);
                }
            };

            const run = async () => {
                let failures = 0;
                let hasRefreshed = false;

                /* eslint-disable no-await-in-loop -- reconnect attempts are sequential by definition */
                while (!isSettled) {
                    const attempt = { openedAt: 0 };
                    let failure: SseFailure;

                    try {
                        await connectOnce(attempt);

                        // pipeTo resolved, so the server called complete(). Clean EOF is success.
                        finish('completed');

                        return;
                    } catch (error) {
                        if (isSettled) {
                            return;
                        }

                        failure = toFailure(error);
                    }

                    // The backend sends a snapshot the instant you connect, so receiving an event
                    // proves nothing. Only a connection that outlived an idle window earns a fresh budget.
                    if (attempt.openedAt > 0 && Date.now() - attempt.openedAt > idleTimeoutMs * 2) {
                        failures = 0;
                        hasRefreshed = false;
                    }

                    const action = classifyFailure(failure);

                    if (action === 'fatal') {
                        console.error(`SSE stream failed: ${label}`, failure);
                        finish('failed');

                        return;
                    }

                    if (action === 'refresh') {
                        if (hasRefreshed || !(await ProxyServerConnector.refreshToken())) {
                            console.error(`SSE stream unauthorized: ${label}`);
                            finish('failed');

                            return;
                        }

                        hasRefreshed = true;
                        continue;
                    }

                    failures += 1;

                    if (failures > MAX_CONSECUTIVE_FAILURES) {
                        console.error(`SSE stream gave up after ${failures} failures: ${label}`, failure);
                        finish('failed');

                        return;
                    }

                    await wait(backoffDelayMs(failures - 1), subscription.signal);
                }
                /* eslint-enable no-await-in-loop */
            };

            run().catch((error) => {
                console.error(`SSE stream crashed: ${label}`, error);
                finish('failed');
            });

            return unsubscribe;
        },
    };
}
